import { Link, useParams } from 'react-router-dom';
import { useNotifications, useOrderDetail, useOrderDocuments } from '@/lib/queries';
import { ChefNav } from '@/components/ChefNav';
import { StatusBadge, orderStatusTone, humaniseStatus } from '@/components/ui/badge';
import { channelLabel, formatDateTime, formatQuantity } from '@/lib/utils';
import { locationLabel } from '@/lib/locations';

// Order Detail & Audit Trail — full line list (supplier visible for audit),
// dispatch status, and a chronological audit timeline.
export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const { data } = useOrderDetail(id);
  const { data: docs } = useOrderDocuments(id);
  const { data: notifications } = useNotifications();
  const order = data?.data;
  const documents = docs?.data ?? [];

  return (
    <div className="min-h-screen bg-canvas">
      <ChefNav notifications={notifications?.data ?? []} />
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6">
        <nav className="mb-4 text-sm text-muted">
          <Link to="/app/history" className="hover:text-brand">
            Order History
          </Link>
          <span className="mx-2">›</span>
          <span className="text-brand">Order {order?.orderNumber ?? ''}</span>
        </nav>

        {!order ? (
          <p className="text-muted">Loading order…</p>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="space-y-8 lg:col-span-2">
              <div className="card p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="font-mono text-xl text-brand">{order.orderNumber}</div>
                    <div className="font-head text-lg font-extrabold uppercase text-ink">
                      {order.locationName}
                    </div>
                  </div>
                  <StatusBadge tone={orderStatusTone(order.status)}>{humaniseStatus(order.status)}</StatusBadge>
                </div>
                <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="eyebrow">Placed By</dt>
                    <dd className="text-ink">{order.placedBy}</dd>
                  </div>
                  <div>
                    <dt className="eyebrow">Submitted</dt>
                    <dd className="text-ink">{formatDateTime(order.submittedAt)}</dd>
                  </div>
                  {order.purchaseOrders.length > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="eyebrow">Supplier POs</dt>
                      <dd className="flex flex-wrap gap-x-6 gap-y-1 text-ink">
                        {order.purchaseOrders.map((po) => (
                          <span key={po.id}>
                            <span className="font-mono text-brand">{po.poNumber ?? '—'}</span>{' '}
                            <span className="text-muted">{po.supplierName}</span>
                          </span>
                        ))}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="card p-6">
                <h2 className="section-head mb-4">Line Items</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-line text-muted">
                        <th className="py-2 pr-4 font-medium">Item</th>
                        <th className="py-2 pr-4 font-medium">Qty</th>
                        <th className="py-2 pr-4 font-medium">Supplier</th>
                        <th className="py-2 pr-4 font-medium">SKU</th>
                        <th className="py-2 pr-4 font-medium">Channel</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.lines.map((l) => (
                        <tr key={l.id} className="border-b border-line">
                          <td className="py-3 pr-4 text-ink">{l.productName}</td>
                          <td className="py-3 pr-4 text-ink">
                            {formatQuantity(l.quantity)} {l.unitOfMeasure}
                          </td>
                          <td className="py-3 pr-4 text-ink">{l.supplierName}</td>
                          <td className="py-3 pr-4 font-mono text-muted">{l.supplierSku}</td>
                          <td className="py-3 pr-4 text-muted">{channelLabel(l.dispatchChannel)}</td>
                          <td className="py-3 pr-4">
                            <StatusBadge tone={orderStatusTone(l.status)}>{humaniseStatus(l.status)}</StatusBadge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="card p-6">
                <h2 className="section-head mb-4">Audit Timeline</h2>
                {order.auditEvents.length === 0 ? (
                  <p className="text-sm text-muted">No events yet.</p>
                ) : (
                  <ol className="relative space-y-4 border-l border-line pl-4">
                    {[...order.auditEvents].reverse().map((e) => (
                      <li key={e.id} className="relative">
                        <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-brand" />
                        <div className="text-sm font-medium text-ink">{humaniseStatus(e.eventType)}</div>
                        <div className="text-xs text-faint">{formatDateTime(e.createdAt)}</div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div className="card p-6">
                <h2 className="section-head mb-4">Documents</h2>
                {documents.length === 0 ? (
                  <p className="text-sm text-muted">No inbound documents yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {documents.map((d) => (
                      <li key={d.id}>
                        <a href={d.downloadUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                          {d.rawFilename ?? d.documentType}
                        </a>
                        <div className="text-xs text-faint">{formatDateTime(d.receivedAt)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
